import { LineData, SlopeData } from '../design/charts';

export const capitalize = (message: string) => {
  const [firstChar, ...rest] = message;
  return `${firstChar.charAt(0).toUpperCase()}${rest.join('')}`;
};

export const createLowerCamelCase = (message: string) => {
  const [first, ...rest] = message.split(' ');
  return `${first.toLocaleLowerCase()}${rest.map(capitalize).join('')}`;
};

export function isDefined<T>(value: T | undefined): value is T {
  return value !== undefined;
}

export const sanitizeInput = (input: string): string => {
  return input.replace(/\s+/g, ' ').trim();
};

export const checkForHTMLTags = (input: string): string | null => {
  const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(input);
  if (hasHtmlTags) {
    return 'HTML tags are not allowed';
  }
  return null;
};
export const checkForValidLabelString = (input: string): string | null => {
  const valid = /^[A-Za-z0-9\-.,_ ]*$/.test(input);
  if (!valid) {
    return 'Symbols are not allowed';
  }
  return null;
};

export const stringifyValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'function') {
    return '[Function]';
  }

  if (typeof value === 'symbol') {
    return value.toString();
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '[Unserializable Object]';
    }
  }
  return String(value);
};

/**
 * Calculate the space needed for a text label based on the number of characters
 * @param text - the text to calculate the space for
 * @returns the space needed for the text label
 */
export const calculateSpaceByCharacter = (text: string | null | undefined): number => {
  if (!text) {
    return 0;
  }
  const spacePerCharacter = 9;
  return text.length * spacePerCharacter;
};

/**
 * Calculate the width needed for the main graph section
 * to ensure the labels are not overlapping
 * @param data - the chart's data
 * @returns the width of the main/middle section graph
 */
export const getMainGraphWidth = (data: SlopeData & LineData, includeSideLabel = false) => {
  const gap = includeSideLabel ? 40 : 20;
  const minimumWidth = 80;

  const comparisonPeriod = data.labels.length || 1;

  const labels = [
    ...(data.summary?.values?.formatted || []),
    ...(data.summary?.changes?.formatted || []),
    ...(data.additionalSummary?.values?.formatted || []),
    ...(data.additionalSummary?.changes?.formatted || []),
    ...(data.labels?.flatMap(label => label) || []),
  ];

  const labelsWidth = labels.map(label => calculateSpaceByCharacter(label) / 2);
  const maxLabelWidth = Math.max(...labelsWidth);

  let calculatedWidth = (maxLabelWidth + gap) * comparisonPeriod;

  if (includeSideLabel) {
    const values = data.sets.flatMap(set => set.values.formatted);
    const valuesWidth = values.map(label => calculateSpaceByCharacter(label) / 2);
    const maxValuesLabelWidth = Math.max(...valuesWidth);
    calculatedWidth += maxValuesLabelWidth;
  }

  return Math.max(calculatedWidth, minimumWidth);
};

/**
 * @param isShared - true if from shared functionality, false otherwise
 * @returns string indicating if the preference is shared
 */

export const isSharedPreference = (isShared: boolean | undefined): string => {
  return isShared ? '?shared=true' : '';
};
